// Styles
import "./campaigns.scss";
// Tools
import { useDispatch } from "react-redux";
import {
	useState,
	useEffect,
	useMemo,
	useCallback,
	startTransition,
} from "react";
// Components
import { CampaignsHeader } from "./components/campaigns-header/CampaignsHeader";
import { CampaignRow } from "./components/campaigns-row/CampaignRow";
import { CircleLoader } from "../../../../../../../../shared/components/circle-loader/CircleLoader";
import { CompactSelect } from "../../../../../../../../shared/components/selects/compact-select/CompactSelect";
import { CompactTextInput } from "../../../../../../../../shared/components/inputs/compact-text-input/CompactTextInput";
import { VirtualList } from "../../../../../../../../shared/components/virtual-list/VirtualList";
import { EmptyList } from "../../../../../../../../shared/components/empty-list/EmptyList";
// Utils
import { hideModuleThunk } from "../../../../utils/module-utils/hide-module";
import {
	buildSelectStatuses,
	AccountSelectItem,
} from "../../../../utils/component-utils/build-select-statuses";
import {
	buildPreparedCampaignsIndex,
	filterCampaignsByStatus,
	filterCampaignsBySearch,
	sortCampaigns,
	PreparedCampaign,
} from "./utils/processCampaigns";
import {
	setSortParameters,
	getSortParameters,
} from "./utils/setSortParameters";
import {
	setFilterParameters,
	getFilterParameters,
} from "./utils/setFilterParameters";
import { sumCostColumnUsd } from "../../../../../../../../shared/utils/sum-cost-column-usd";
// Service
import { useDashboardData } from "../../../../../../service/dashboard-data.service";

export const Campaigns = () => {
	const dispatch = useDispatch<any>();

	const [accountSelectItems, setAccountSelectItems] = useState<
		AccountSelectItem[]
	>([]);

	const [selectedStatus, setSelectedStatus] = useState<string>(
		getFilterParameters() ?? "all",
	);
	const [campaignSearch, setCampaignSearch] = useState<string>("");
	const adsImage = "/img/icons/large-64/click.png";

	// Критерий и способ сортировки (переносим логику сюда)
	const [sortInfo, setSortInfo] = useState({
		sortMethod: getSortParameters().sortMethod ?? "asc",
		sortBy: getSortParameters().sortBy ?? "status",
	});

	// ------ Data
	const { data, isAwaitingDashboardContent } = useDashboardData();
	const campaignsData = data?.campaigns_info?.data;

	// ------ Functions
	const handleSelect = (key: string) => {
		startTransition(() => {
			setSelectedStatus(key);
			setFilterParameters(key);
		});
	};
	const handleSort = (method: string, by: string) => {
		setSortInfo({ sortMethod: method, sortBy: by });
		setSortParameters(method, by);
	};

	// ------ Effects
	// Формируем список статусов для выпадающего списка
	useEffect(() => {
		buildSelectStatuses(
			campaignsData,
			"campaign_status",
			setAccountSelectItems,
		);
	}, [campaignsData]);

	const { prepared, byStatus } = useMemo(
		() => buildPreparedCampaignsIndex(campaignsData),
		[campaignsData],
	);

	const filteredByStatus = useMemo(
		() => filterCampaignsByStatus(prepared, byStatus, selectedStatus),
		[prepared, byStatus, selectedStatus],
	);

	const filteredBySearch = useMemo(
		() => filterCampaignsBySearch(filteredByStatus, campaignSearch),
		[filteredByStatus, campaignSearch],
	);

	const pinNew = selectedStatus === "all" && campaignSearch.trim() === "";

	const sortedCampaigns = useMemo(
		() => sortCampaigns(filteredBySearch, sortInfo, pinNew),
		[filteredBySearch, sortInfo, pinNew],
	);

	const campaignsTotalCostUsd = useMemo(
		() => sumCostColumnUsd(sortedCampaigns),
		[sortedCampaigns],
	);

	const renderCampaignRow = useCallback(
		(item: PreparedCampaign) => (
			<CampaignRow
				key={item.campaign_id}
				adsName={item.campaign_name ?? ""}
				accountName={item.account_name ?? ""}
				accountId={item.account_id ?? ""}
				appId={item.app_id ?? ""}
				status={String(item.campaign_status ?? "")}
				cost={Number(item.cost_usd) || 0}
				isNew={item._isNew}
				campaignId={item._campaignIdNum}
				onOpen={(id) => {
					const url = new URL(`/main/campaign/${id}`, window.location.origin);
					window.open(url.toString(), "_blank", "noopener,noreferrer");
				}}
			/>
		),
		[],
	);

	const emptyListText =
		campaignSearch.trim().length > 0
			? "За вашим запитом нічого не знайдено"
			: "Рекламні кампанії відсутні";

	return (
		<div className='campaigns window'>
			<div className='window__header'>
				<div className='window__header_left'>
					<img src='/img/icons/report.png' alt='campaigns icon' />
					<div>Кампанії</div>
				</div>
				<div className='window__header_center campaigns__header-center'>
					<CompactTextInput
						className='campaigns__header-search'
						value={campaignSearch}
						actionInput={setCampaignSearch}
						placeholder='Пошук: назва, ID, акаунт'
					/>
					<CompactSelect
						items={accountSelectItems}
						value={selectedStatus}
						onSelect={handleSelect}
					/>
				</div>
				<div className='window__header_right'>
					<div
						className='window__header_right_close-button'
						role='button'
						aria-label='hide campaigns'
						onClick={() => dispatch(hideModuleThunk("campaigns"))}>
						<img src='/img/icons/close.png' alt='close button' />
					</div>
				</div>
			</div>
			<div className='window__body'>
				<CampaignsHeader
					setSortInfo={sortInfo}
					handleSort={handleSort}
					totalCostUsd={campaignsTotalCostUsd}
				/>
				{isAwaitingDashboardContent ? (
					<div className='window__loader'>
						<CircleLoader />
					</div>
				) : (
					<div className='window__body_list'>
						<div className='window__body_list-inner'>
							<VirtualList
								items={sortedCampaigns}
								overscan={6}
								renderItem={renderCampaignRow}
							/>
						</div>
					</div>
				)}
				{!isAwaitingDashboardContent && sortedCampaigns.length === 0 && (
					<div className='window__loader'>
						<EmptyList image={adsImage} text={emptyListText} />
					</div>
				)}
			</div>
		</div>
	);
};
